import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { useTranslation } from '../i18n';
import { PageHeader, Section } from '../components/Section';
import { RecentGenerationsStrip } from '../components/RecentGenerationsStrip';
import { JobResult } from '../components/JobResult';
import { getFingerprintNow } from '../hooks/useFingerprint';
import { useSignupNudge } from '../components/SignupNudge';
import { usePriceQuote } from '../hooks/usePriceQuote';

type Model = 'z-image-text-to-image' | 'nano-banana' | 'nano-banana-2';

const ASPECTS = ['16:9', '4:3', '1:1', '3:4', '9:16'] as const;

const isNanoBanana = (m: Model) => m.startsWith('nano-banana');

export default function TextToImage() {
  const t = useTranslation();
  const location = useLocation();
  const remix = (location.state as { remix?: Record<string, unknown> } | null)?.remix ?? null;

  const [model, setModel] = useState<Model>((remix?.model as Model | undefined) ?? 'z-image-text-to-image');
  const [prompt, setPrompt] = useState((remix?.prompt as string | undefined) ?? '');
  const [aspect, setAspect] = useState<(typeof ASPECTS)[number]>((remix?.aspect_ratio as (typeof ASPECTS)[number] | undefined) ?? '1:1');
  const [minHeight, setMinHeight] = useState<480 | 720 | 1080>((remix?.min_height as 480 | 720 | 1080 | undefined) ?? 720);
  const [numImages, setNumImages] = useState((remix?.num_images as number | undefined) ?? 1);
  const [submitting, setSubmitting] = useState(false);
  const [activityId, setActivityId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = prompt.trim().length > 0 && !submitting;
  // num_images only applies to nano-banana; z-image always returns a single image.
  const effectiveImages = isNanoBanana(model) ? numImages : 1;
  const perImage = usePriceQuote({ endpoint: 'text-to-image', model, minHeight });
  const credits = perImage != null ? perImage * effectiveImages : null;

  const { gateGenerate } = useSignupNudge();

  const handleSubmit = async () => {
    // Anonymous users get the signup nudge instead of a doomed 402.
    if (gateGenerate()) return;
    setSubmitting(true);
    setError(null);
    try {
      const fingerprint = await getFingerprintNow();
      const res = await fetch('/api/v1/text-to-image/generate-async', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt,
          model,
          aspect_ratio: aspect,
          min_height: minHeight,
          num_images: effectiveImages,
          fingerprint,
        }),
      });
      if (res.status === 402) {
        const body = await res.json();
        throw new Error(`Insufficient credits (need ${body.required}, have ${body.available})`);
      }
      if (!res.ok) {
        const body = await res.text().catch(() => '');
        throw new Error(`submit failed: ${res.status} ${body}`);
      }
      const data = await res.json();
      setActivityId(data.activity_id);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setActivityId(null);
    setPrompt('');
  };

  return (
    <Box>
      <PageHeader title={t.textToImage.title} description={t.textToImage.description} />
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>

      {activityId ? (
        <Section title={t.textToImage.resultTitle}>
          <Paper sx={{ p: 3 }}>
            <JobResult
              activityId={activityId}
              resultType="image"
              onReset={reset}
              resetLabel={t.textToImage.newGeneration}
            />
          </Paper>
        </Section>
      ) : (
        <>
          <Section title={t.textToImage.modelTitle}>
            <Paper sx={{ p: 3 }}>
              <Stack spacing={2.5}>
                <TextField
                  select
                  label={t.textToImage.model}
                  value={model}
                  onChange={(e) => setModel(e.target.value as Model)}
                  size="small"
                  sx={{ maxWidth: 320 }}
                >
                  <MenuItem value="z-image-text-to-image">Z-Image Text-to-Image</MenuItem>
                  <MenuItem value="nano-banana" disabled>Nano Banana (coming soon…)</MenuItem>
                  <MenuItem value="nano-banana-2" disabled>Nano Banana 2 (coming soon…)</MenuItem>
                </TextField>
                <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
                  <TextField
                    select
                    label={t.textToImage.aspectRatio}
                    value={aspect}
                    onChange={(e) => setAspect(e.target.value as (typeof ASPECTS)[number])}
                    size="small"
                    sx={{ minWidth: 160 }}
                  >
                    {ASPECTS.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    label={t.textToImage.resolution}
                    value={minHeight}
                    onChange={(e) => setMinHeight(Number(e.target.value) as 480 | 720 | 1080)}
                    size="small"
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value={480}>480p</MenuItem>
                    <MenuItem value={720}>720p</MenuItem>
                    <MenuItem value={1080}>1080p</MenuItem>
                  </TextField>
                  {isNanoBanana(model) && (
                    <TextField
                      select
                      label={t.textToImage.numImages}
                      value={numImages}
                      onChange={(e) => setNumImages(Number(e.target.value))}
                      size="small"
                      sx={{ minWidth: 160 }}
                    >
                      {[1, 2, 3, 4].map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </Stack>
              </Stack>
            </Paper>
          </Section>

          <Section title={t.textToImage.prompt}>
            <Paper sx={{ p: 3 }}>
              <TextField
                fullWidth
                placeholder={t.textToImage.promptPlaceholder}
                multiline
                minRows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </Paper>
          </Section>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" size="large" disabled={!canSubmit} onClick={handleSubmit}>
              {submitting ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                `${t.textToImage.generate}${credits != null ? ` (${credits} ${t.credits.balanceUnit})` : ''}`
              )}
            </Button>
          </Stack>
        </>
      )}
      </Box>
      <RecentGenerationsStrip endpoint="text-to-image" />
      </Box>
    </Box>
  );
}
